import mustache from "mustache";
import { HistoricoLoteItem } from "../../../../../modulos/pesaje/ganaderia/services/HistoricoLoteGetListService";
import { RotacionPesajeVeterinariaResponse } from "../../../../../modulos/pesaje/ganaderia/response/RotacionPesajeVeterinariaResponse";
import { formatFechaCorta } from "../../../../../utils/fechaUtils";
import { _2d, _miles } from "../../../../../utils/numberUtils";
import { IConnectRender } from "../../../../definitions/IConnectRender";
import template from "./historicoLoteListItemTemplate.html";

mustache.parse(template);

/** Contador para ids únicos por instancia. */
let HISTORICO_LOTE_ITEM_ID = 0;

/** Fila del histórico de un lote (Render/Connect). Adaptativa según el tipo de
 *  movimiento: compra (ingreso), venta (salida) o evento RPV (rotación
 *  entrante/saliente, pesaje, veterinaria), con un badge-ícono por acción y la
 *  acción "ver" que abre el detalle del registro fuente. */
export class HistoricoLoteListItemRender implements IConnectRender {
  /** Id único de la instancia (para los ids del DOM). */
  private readonly _itemId: number;

  /**
   * @param _item   Movimiento a mostrar.
   * @param _onView Abre el detalle del registro fuente.
   */
  constructor(
    private readonly _item: HistoricoLoteItem,
    private readonly _onView: (item: HistoricoLoteItem) => void,
  ) {
    HISTORICO_LOTE_ITEM_ID += 1;
    this._itemId = HISTORICO_LOTE_ITEM_ID;
  }

  /** HTML de la fila (según el tipo de movimiento). */
  render(): string {
    const item = this._item;
    let primario = "";
    let secundario = "";
    let withCompra = false;
    let withVenta = false;
    let withRotacion = false;
    let withPesaje = false;
    let withVeterinaria = false;

    if (item.tipo === "compra") {
      withCompra = true;
      primario = "Compra de animales";
      secundario = [
        `+${item.compra.cantidadAnimalesCompra} animales`,
        `prom ${_2d(item.compra.pesoPromedioEntrada)} kg`,
      ].join(" · ");
    } else if (item.tipo === "venta") {
      withVenta = true;
      primario = "Venta de animales";
      secundario = [
        `−${item.venta.cantidadAnimales} animales`,
        `${_miles(item.venta.pesoTotal)} kg`,
      ].join(" · ");
    } else {
      const evento = item.evento;
      withRotacion = evento.isRotacion;
      withPesaje = evento.isPesaje;
      withVeterinaria = evento.isVeterinaria;
      primario = this._primarioRpv(evento, item.esIngresoPorRotacion);
      secundario = this._secundarioRpv(evento, item.esIngresoPorRotacion);
    }

    return mustache.render(template, {
      id: this._itemId,
      fecha: formatFechaCorta(item.fecha),
      primario: primario,
      secundario: secundario,
      withCompra: withCompra,
      withVenta: withVenta,
      withRotacion: withRotacion,
      withPesaje: withPesaje,
      withVeterinaria: withVeterinaria,
    });
  }

  /** Cablea el botón "ver". */
  connect(): void {
    this._view = document.getElementById(`historico-view-${this._itemId}`);
    this._view?.addEventListener("click", this._onViewClick);
  }

  /** Libera el listener. */
  dispose(): void {
    this._view?.removeEventListener("click", this._onViewClick);
  }

  private _view: HTMLElement | null = null;

  private _onViewClick = (): void => this._onView(this._item);

  /** Título del evento RPV según rotación/acciones activas. */
  private _primarioRpv(
    evento: RotacionPesajeVeterinariaResponse,
    esIngreso: boolean,
  ): string {
    if (evento.isRotacion) {
      return esIngreso ? "Ingreso por rotación" : "Salida por rotación";
    }
    const acciones: string[] = [];
    if (evento.isPesaje) acciones.push("Pesaje");
    if (evento.isVeterinaria) acciones.push("Veterinaria");
    return acciones.join(" / ") || "Evento";
  }

  /** Detalle del evento RPV: dirección de la rotación + cantidad + acciones. */
  private _secundarioRpv(
    evento: RotacionPesajeVeterinariaResponse,
    esIngreso: boolean,
  ): string {
    const partes: string[] = [];
    if (evento.isRotacion) {
      partes.push(
        esIngreso
          ? `desde ${evento.loteOrigenNombre}`
          : `hacia ${evento.loteDestinoNombre ?? "—"}`,
      );
    }
    partes.push(`${evento.cantidadAnimales} animales`);
    if (evento.isPesaje && evento.pesoPromedio != null) {
      partes.push(`prom ${_2d(evento.pesoPromedio)} kg`);
    }
    if (evento.isVeterinaria) {
      const n = evento.procedimientoIds.length;
      partes.push(`${n} ${n === 1 ? "procedimiento" : "procedimientos"}`);
    }
    return partes.join(" · ");
  }
}
